import React, { useEffect, useRef, useState, useCallback } from "react";
import { Moon, Sun, Activity, Cpu, Clock, AlertTriangle, PanelLeftOpen, PanelLeftClose } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";

function Pill({ icon: Icon, label, value, ok, testid }) {
  const color = ok ? "var(--accent)" : "var(--danger)";
  return (
    <div
      className="flex items-center gap-1.5 px-2 py-1 rounded-full border border-[var(--bd)] bg-[var(--surface2)]"
      data-testid={testid}
    >
      <span className="w-2 h-2 rounded-full pulse-dot" style={{ background: color }} />
      <Icon size={13} className="text-[var(--t2)]" />
      <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--t2)] hidden sm:inline">{label}</span>
      <span className="font-mono text-xs font-medium" style={{ color }}>{value}</span>
    </div>
  );
}

export function Header({ collapsed, onToggle }) {
  const { theme, toggleTheme, mqttConnected, telemetry, lastUpdate, settings } = useApp();
  const { user } = useAuth();
  const updated = lastUpdate ? lastUpdate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "--:--:--";
  const welcomeSpokenRef = useRef(false);

  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > lastY.current && y > 64);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const speakWelcome = useCallback((text) => {
    if (!window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = settings?.speechRate || 1;
    window.speechSynthesis.speak(u);
  }, [settings]);

  useEffect(() => {
    if (!user || welcomeSpokenRef.current) return;
    welcomeSpokenRef.current = true;

    const timer = setTimeout(() => {
      const name = user.displayName || user.email?.split("@")[0] || "operator";
      let statusMsg;
      if (mqttConnected && telemetry) {
        statusMsg = `Welcome back ${name}. System is live. ` +
          `MQTT connected. ` +
          `${telemetry.temp ? `Temperature ${telemetry.temp} degrees. ` : ""}` +
          `${telemetry.v1 ? `Phase 1 voltage ${telemetry.v1} volts, power ${telemetry.p1} watts. ` : ""}` +
          `${telemetry.freq ? `Frequency ${telemetry.freq} hertz. ` : ""}` +
          `ESP32 ${telemetry.online ? "online" : "waiting"}. ` +
          `Campus Energy Monitor standing by.`;
      } else if (mqttConnected) {
        statusMsg = `Welcome back ${name}. MQTT broker connected. Waiting for ESP32 data on campus energy state topic. System is on standby.`;
      } else {
        statusMsg = `Welcome back ${name}. Warning: MQTT broker is disconnected. Attempting reconnect to HiveMQ Cloud. Sensor data currently unavailable. System in standby mode.`;
      }
      speakWelcome(statusMsg);
    }, 2000);

    return () => clearTimeout(timer);
  }, [user, mqttConnected, telemetry, speakWelcome]);

  return (
    <header className={`sticky top-0 z-40 backdrop-blur-xl bg-[var(--bg)]/80 border-b border-[var(--bd)] transition-transform duration-300 ${hidden ? "-translate-y-full" : "translate-y-0"}`} data-testid="app-header">
      <div className="w-full px-3 sm:px-4 py-2 flex items-center gap-2 min-h-[52px]">

        {/* Sidebar toggle - left */}
        <button
          onClick={onToggle}
          data-testid="sidebar-toggle"
          className="shrink-0 w-9 h-9 rounded-md border border-[var(--bd)] bg-[var(--surface2)] flex items-center justify-center transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)] active:scale-95"
          aria-label="Toggle sidebar"
        >
          {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>

        {/* Center title - flex-1 so it fills available space */}
        <div className="flex-1 text-center min-w-0">
          <h1 className="font-heading font-black text-sm sm:text-base lg:text-lg tracking-tighter flex items-center justify-center gap-1.5 leading-tight">
            <span className="text-[var(--accent)]">⚡</span>
            <span className="truncate">Campus Energy Monitor</span>
          </h1>
          <p className="text-[9px] sm:text-[10px] text-[var(--t2)] tracking-wide truncate hidden sm:block">
            Cloud Dashboard · Cloudflare Pages · CPUT 2026
          </p>
        </div>

        {/* Right: pills (lg+) + theme toggle */}
        <div className="shrink-0 flex items-center gap-1.5">
          <div className="hidden lg:flex items-center gap-1.5">
            <Pill icon={Activity} label="MQTT" value={mqttConnected ? "LIVE" : "OFFLINE"} ok={mqttConnected} testid="pill-mqtt" />
            <Pill icon={Cpu} label="ESP32" value={telemetry?.online ? "ONLINE" : "WAIT"} ok={!!telemetry?.online} testid="pill-esp32" />
            <Pill icon={Clock} label="Update" value={updated} ok={mqttConnected} testid="pill-update" />
          </div>
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="w-9 h-9 rounded-md border border-[var(--bd)] bg-[var(--surface2)] flex items-center justify-center transition-colors hover:border-[var(--bd-strong)] active:scale-95"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </div>

      {/* mobile/tablet pills - compact, scrollable */}
      <div className="lg:hidden flex justify-start gap-1.5 px-3 pb-2 overflow-x-auto hide-scrollbar">
        <Pill icon={Activity} label="MQTT" value={mqttConnected ? "LIVE" : "OFF"} ok={mqttConnected} testid="pill-mqtt-m" />
        <Pill icon={Cpu} label="ESP32" value={telemetry?.online ? "ON" : "WAIT"} ok={!!telemetry?.online} testid="pill-esp32-m" />
        <Pill icon={Clock} label="UPD" value={updated} ok={mqttConnected} testid="pill-update-m" />
      </div>

      {/* Always-visible ticker - live data when connected, warning when offline */}
      <div
        className="overflow-hidden"
        style={{ background: "#FFCC00" }}
        data-testid="mqtt-warning-banner"
      >
        <div className="marquee-track py-1.5">
          {[0, 1].map((k) => (
            <span key={k} className="font-mono text-xs font-bold tracking-wide px-6 flex items-center gap-3 text-black">
              {mqttConnected ? (
                telemetry ? (
                  <>
                    <span>⚡ LIVE</span>
                    <span>·</span>
                    <span>V1 {telemetry.v1 ?? "--"}V</span>
                    <span>·</span>
                    <span>V2 {telemetry.v2 ?? "--"}V</span>
                    <span>·</span>
                    <span>V3 {telemetry.v3 ?? "--"}V</span>
                    <span>·</span>
                    <span>P1 {telemetry.p1 ?? "--"}W</span>
                    <span>·</span>
                    <span>P2 {telemetry.p2 ?? "--"}W</span>
                    <span>·</span>
                    <span>P3 {telemetry.p3 ?? "--"}W</span>
                    <span>·</span>
                    <span>TEMP {telemetry.temp ?? "--"}°C</span>
                    <span>·</span>
                    <span>FREQ {telemetry.freq ?? "--"}Hz</span>
                    <span>·</span>
                    <span>HiveMQ CONNECTED · CPUT ENERGY MONITOR · ESP32 ONLINE</span>
                  </>
                ) : (
                  <>⚡ MQTT CONNECTED · WAITING FOR ESP32 DATA · campus/energy/state · CPUT CAMPUS ENERGY MONITOR · STANDBY</>
                )
              ) : (
                <><AlertTriangle size={13} /> MQTT BROKER DISCONNECTED - ATTEMPTING RECONNECT TO HIVEMQ CLOUD · SENSOR DATA UNAVAILABLE · STANDBY MODE</>
              )}
            </span>
          ))}
        </div>
      </div>
    </header>
  );
}
